import * as Api from '@ViewModels';
import { Moment } from 'moment';
import * as React from 'react';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import {
	applyFacebookMentions,
	convertHtmlStringValueToPlainText,
	createContentStateWithHtmlStringValue,
	getShortenedString,
	mergeFacebookMentions,
	parseFacebookMentions,
} from '../../../../models/UiUtils';
import {
	IPlatformCustomizationDraft,
	applyBaseAttachmentsToCustomizations,
	createCustomizationFromBase,
	fromApiPlatformCustomizations,
	getActivePlatform,
	getTargetedPlatforms,
	materializePlatformCustomizations,
	replaceAttachmentEverywhere,
	toApiPlatformCustomizations,
} from '../../../../models/utils/platformCustomizations';
import { useUserSession } from '../../../../models/hooks/appStateHooks';

export interface IEditSocialMediaPostContext {
	aiReference?: Api.IAIReference;
	/** Internally set to true any time content or images change */
	hasChanges?: boolean;
	hasVideo?: boolean;
	postContent?: string;
	postImages?: Api.IFileAttachment[];
	/** Pages picked from the mention dropdown; each "@Page Name" in postContent that names one is stored as its token. */
	facebookMentions?: Api.IFacebookPageLookupResult[];
	postTargets?: Api.IPostTarget[];
	campaignName?: string;
	isReviewMode?: boolean;
	schedulePost?(
		selectedMoment?: Moment,
		suggestionId?: string,
		impersonationContext?: Api.IImpersonationContext,
		bypassCompliance?: boolean,
		sendWithComplianceEmail?: string,
		forceSendWithCompliance?: boolean
	): Promise<readonly [Api.SocialMediaPostViewModel, Api.IOperationResultNoValue]>;
	selectedCreator?: Api.IUser;
	setAIReference?(aiReference: Api.IAIReference | ((currentValue: Api.IAIReference) => Api.IAIReference)): void;
	setContent?(postContent: string | ((currentValue: string) => string)): void;
	setCampaignName?(campaignName: string | ((currentValue: string) => string)): void;
	setHasChanges?(hasChanges: boolean | ((currentValue: boolean) => boolean)): void;
	setIsReviewMode?(isReviewMode: boolean | ((currentValue: boolean) => boolean)): void;
	setImages?(
		postImages?: Api.IFileAttachment[] | ((currentValue: Api.IFileAttachment[]) => Api.IFileAttachment[])
	): void;
	/**
	 * Swaps one attachment for another in place, keeping its position in the pool and its routing on
	 * every platform version. For an edit that returns a new attachment for the same picture — a crop —
	 * where `setImages` would read the new id as an addition and drop the original from every version.
	 */
	replaceImage?(originalId: string, replacement: Api.IFileAttachment): void;
	setFacebookMentions?(
		facebookMentions?:
			| Api.IFacebookPageLookupResult[]
			| ((currentValue: Api.IFacebookPageLookupResult[]) => Api.IFacebookPageLookupResult[])
	): void;
	/** Loads stored content: its mention tokens become "@Page Name" text and the mentioned Pages. */
	loadContent?(content?: string): void;
	setPostTargets?(postTargets?: Api.IPostTarget[] | ((currentValue: Api.IPostTarget[]) => Api.IPostTarget[])): void;
	setSelectedCreator?(selectedCreator: Api.IUser | ((currentValue: Api.IUser) => Api.IUser)): void;
	setTemplate?(template?: Api.ITemplate | ((currentValue: Api.ITemplate) => Api.ITemplate)): void;
	template?: Api.ITemplate;
	updatePost?(
		// targets: Api.IPostTarget[],
		postOrId: string | Api.SocialMediaPostViewModel,
		selectedMoment?: Moment,
		bypassCompliance?: boolean,
		sendWithComplianceEmail?: string,
		forceSendWithCompliance?: boolean
	): Promise<readonly [Api.IOperationResult<Api.ISocialMediaPost>, Api.IOperationResultNoValue]>;
	getPostBase?: () => Api.ISocialMediaPostBase;
	overlayLogo?: boolean;
	setOverlayLogo?(overlayLogo: boolean | ((currentValue: boolean) => boolean)): void;
	logoSize?: number;
	setLogoSize?(logoSize: number | ((currentValue: number) => number)): void;
	hideWhiteBackground?: boolean;
	setHideWhiteBackground?(hideWhiteBackground: boolean | ((currentValue: boolean) => boolean)): void;
	/** While true the base post publishes nowhere — every targeted platform publishes its own version. */
	customizeByPlatform?: boolean;
	platformCustomizations?: IPlatformCustomizationDraft[];
	/** Turning this on copies the base into a version per targeted platform; turning it off discards them. */
	setCustomizeByPlatform?(customizeByPlatform: boolean): void;
	setPlatformCustomization?(
		platform: Api.SocialMediaType,
		customization:
			| Partial<IPlatformCustomizationDraft>
			| ((currentValue: IPlatformCustomizationDraft) => Partial<IPlatformCustomizationDraft>)
	): void;
	resetPlatformCustomization?(platform: Api.SocialMediaType): void;
	/**
	 * Seeds the split from a stored post's or template's versions, and adds the Pages they mention to the
	 * picked set, without marking the draft changed. Run after `loadContent`, which replaces that set.
	 */
	loadPlatformCustomizations?(platformCustomizations: readonly Api.IPostPlatformCustomization[] | undefined): void;
	/** The platform version on screen. Shared so the composer and the preview stay on the same one. */
	activePlatform?: Api.SocialMediaType;
	setActivePlatform?(platform: Api.SocialMediaType): void;
}

export const EditSocialMediaPostContext = React.createContext<IEditSocialMediaPostContext>(null);

export const useEditSocialMediaPostContext = () => {
	const context = React.useContext(EditSocialMediaPostContext);
	if (!context) {
		throw new Error(`This component requires the use of "EditSocialMediaPostContext"`);
	}
	return context;
};

export const EditSocialMediaPostContextProvider: React.FC<
	React.PropsWithChildren<{
		contextRef?: React.MutableRefObject<IEditSocialMediaPostContext>;
	}>
> = ({ children, contextRef }) => {
	const [context] = useCreateEditSocialMediaContext();
	if (contextRef) {
		contextRef.current = context;
	}

	return <EditSocialMediaPostContext.Provider value={context}>{children}</EditSocialMediaPostContext.Provider>;
};

export const useCreateEditSocialMediaContext = () => {
	const userSession = useUserSession();
	const setHasChangesRef = React.useRef<(hasChanges?: boolean) => void>(null);
	const setContentRef = React.useRef<(content?: string) => void>(null);
	const setImagesRef = React.useRef<(images?: Api.IFileAttachment[]) => void>(null);
	const replaceImageRef = React.useRef<(originalId: string, replacement: Api.IFileAttachment) => void>(null);
	const setFacebookMentionsRef = React.useRef<(facebookMentions?: Api.IFacebookPageLookupResult[]) => void>(null);
	const loadContentRef = React.useRef<(content?: string) => void>(null);
	const setPostTargetsRef = React.useRef<(targets?: Api.IPostTarget[]) => void>(null);
	const setSelectedCreatorRef = React.useRef<(selectedCreator?: Api.IUser) => void>(null);
	const setTemplateRef = React.useRef<(template?: Api.ITemplate) => void>(null);
	const { postNotification } = useLocalNotificationService<Api.SocialMediaPostViewModel>();
	const setAIReferenceRef = React.useRef<(aiReference?: Api.IAIReference) => void>(null);
	const setCampaignNameRef = React.useRef<(campaignName?: string) => void>(null);
	const setIsReviewModeRef = React.useRef<(isReviewMode?: boolean) => void>(null);
	const setOverlayLogoRef = React.useRef<(overlayLogo?: boolean) => void>(null);
	const setLogoSizeRef = React.useRef<(logoSize?: number) => void>(null);
	const setHideWhiteBackgroundRef = React.useRef<(hideWhiteBackground?: boolean) => void>(null);
	const setCustomizeByPlatformRef = React.useRef<(customizeByPlatform: boolean) => void>(null);
	const setPlatformCustomizationRef =
		React.useRef<(platform: Api.SocialMediaType, next: Partial<IPlatformCustomizationDraft>) => void>(null);
	const resetPlatformCustomizationRef = React.useRef<(platform: Api.SocialMediaType) => void>(null);
	const loadPlatformCustomizationsRef =
		React.useRef<(customizations: readonly Api.IPostPlatformCustomization[] | undefined) => void>(null);
	const setActivePlatformRef = React.useRef<(platform: Api.SocialMediaType) => void>(null);

	const schedulePostRef =
		React.useRef<
			(
				selectedMoment?: Moment,
				suggestionId?: string,
				impersonationContext?: Api.IImpersonationContext,
				bypassCompliance?: boolean,
				sendWithComplianceEmail?: string,
				forceSendWithCompliance?: boolean
			) => Promise<readonly [Api.SocialMediaPostViewModel, Api.IOperationResultNoValue]>
		>(null);
	const updatePostRef =
		React.useRef<
			(
				postOrId: string | Api.SocialMediaPostViewModel,
				selectedMoment?: Moment,
				bypassCompliance?: boolean,
				sendWithComplianceEmail?: string,
				forceSendWithCompliance?: boolean
			) => Promise<readonly [Api.IOperationResult<Api.ISocialMediaPost>, Api.IOperationResultNoValue]>
		>(null);
	const getPostBase = React.useRef(() => {
		const { aiReference, campaignName, template, postContent, postImages, facebookMentions } = contextRef.current;
		const unwrappedContent = convertHtmlStringValueToPlainText(template?.content.document);
		const isCustomized = unwrappedContent !== postContent || template?.attachments !== postImages;
		const wrappedContent = createContentStateWithHtmlStringValue(applyFacebookMentions(postContent, facebookMentions));
		const post: Api.ISocialMediaPostBase = {
			aiReference,
			attachments: postImages,
			content: {
				document: wrappedContent.getRawRichTextContent().document,
			},
			name:
				campaignName ||
				(template?.id ? template.name : postContent ? getShortenedString(postContent, 25, ' ') : 'Custom Post'),
			templateReference: template?.id
				? {
						isCustomized,
						isSystemTemplate: template.scope
							? !![Api.TemplateScope.Industry, Api.TemplateScope.System].find(x => x === template.scope)
							: false,
						name: template.name,
						templateId: template.id,
					}
				: null,
			designatedTargets: contextRef.current?.postTargets,
			overlayLogo: contextRef.current.overlayLogo,
			logoSize: contextRef.current.overlayLogo ? contextRef.current.logoSize : undefined,
			// Carrying versions is what marks a post customized, so split off sends an empty set rather
			// than a flag, and a post never keeps variants it no longer publishes.
			platformCustomizations: contextRef.current.customizeByPlatform
				? toApiPlatformCustomizations(
						contextRef.current.platformCustomizations,
						getTargetedPlatforms(contextRef.current.postTargets),
						facebookMentions
					)
				: [],
		};
		return post;
	}).current;

	const contextRef = React.useRef<IEditSocialMediaPostContext>(null);
	const [context, setContext] = React.useState<IEditSocialMediaPostContext>(() => {
		const ctx: Partial<IEditSocialMediaPostContext> = {
			campaignName: '',
			hasChanges: false,
			hasVideo: false,
			postContent: '',
			postImages: [],
			facebookMentions: [],
			postTargets: [],
			selectedCreator: null,
			template: null,
			overlayLogo: false,
			logoSize: Api.DEFAULT_LOGO_SIZE,
			hideWhiteBackground: false,
			isReviewMode: false,
			customizeByPlatform: false,
			platformCustomizations: [],
		};
		ctx.setAIReference = aiReferenceProvider => {
			setAIReferenceRef.current?.(
				typeof aiReferenceProvider === 'function'
					? aiReferenceProvider(contextRef.current.aiReference)
					: aiReferenceProvider
			);
		};

		ctx.setHasChanges = booleanProvider => {
			setHasChangesRef.current?.(
				typeof booleanProvider === 'function' ? booleanProvider(contextRef.current.hasChanges) : booleanProvider
			);
		};

		ctx.setImages = imageProvider => {
			setImagesRef.current?.(
				typeof imageProvider === 'function' ? imageProvider(contextRef.current.postImages) : imageProvider
			);
		};

		ctx.replaceImage = (originalId, replacement) => replaceImageRef.current?.(originalId, replacement);

		ctx.setFacebookMentions = facebookMentionsProvider => {
			setFacebookMentionsRef.current?.(
				typeof facebookMentionsProvider === 'function'
					? facebookMentionsProvider(contextRef.current.facebookMentions)
					: facebookMentionsProvider
			);
		};

		ctx.setContent = contentProvider => {
			setContentRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.postContent) : contentProvider
			);
		};

		ctx.loadContent = content => {
			loadContentRef.current?.(content);
		};

		ctx.setSelectedCreator = creatorProvider => {
			setSelectedCreatorRef.current?.(
				typeof creatorProvider === 'function' ? creatorProvider(contextRef.current.selectedCreator) : creatorProvider
			);
		};

		ctx.setPostTargets = targetsProvider => {
			setPostTargetsRef.current?.(
				typeof targetsProvider === 'function' ? targetsProvider(contextRef.current.postTargets) : targetsProvider
			);
		};

		ctx.setTemplate = contentProvider => {
			setTemplateRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.template) : contentProvider
			);
		};

		ctx.setCampaignName = contentProvider => {
			setCampaignNameRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.campaignName) : contentProvider
			);
		};

		ctx.setIsReviewMode = contentProvider => {
			setIsReviewModeRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.isReviewMode) : contentProvider
			);
		};

		ctx.setOverlayLogo = overlayLogoProvider => {
			setOverlayLogoRef.current?.(
				typeof overlayLogoProvider === 'function'
					? overlayLogoProvider(contextRef.current.overlayLogo)
					: overlayLogoProvider
			);
		};
		ctx.setLogoSize = logoSizeProvider => {
			setLogoSizeRef.current?.(
				typeof logoSizeProvider === 'function' ? logoSizeProvider(contextRef.current.logoSize) : logoSizeProvider
			);
		};
		ctx.setHideWhiteBackground = hideWhiteBackgroundProvider => {
			setHideWhiteBackgroundRef.current?.(
				typeof hideWhiteBackgroundProvider === 'function'
					? hideWhiteBackgroundProvider(contextRef.current.hideWhiteBackground)
					: hideWhiteBackgroundProvider
			);
		};

		ctx.schedulePost = (
			selectedMoment,
			suggestionId,
			impersonationContext,
			bypassCompliance,
			sendWithComplianceEmail,
			forceSendWithCompliance
		) =>
			schedulePostRef.current?.(
				selectedMoment,
				suggestionId,
				impersonationContext,
				bypassCompliance,
				sendWithComplianceEmail,
				forceSendWithCompliance
			);

		ctx.setCustomizeByPlatform = customizeByPlatform => setCustomizeByPlatformRef.current?.(customizeByPlatform);

		ctx.setPlatformCustomization = (platform, customizationProvider) => {
			const current =
				contextRef.current.platformCustomizations?.find(customization => customization.platform === platform) ??
				createCustomizationFromBase(platform, contextRef.current.postContent, contextRef.current.postImages);
			setPlatformCustomizationRef.current?.(
				platform,
				typeof customizationProvider === 'function' ? customizationProvider(current) : customizationProvider
			);
		};

		ctx.resetPlatformCustomization = platform => resetPlatformCustomizationRef.current?.(platform);

		ctx.loadPlatformCustomizations = platformCustomizations =>
			loadPlatformCustomizationsRef.current?.(platformCustomizations);

		ctx.setActivePlatform = platform => setActivePlatformRef.current?.(platform);

		ctx.getPostBase = getPostBase;
		ctx.updatePost = (postOrId, selectedMoment, bypassCompliance, sendWithComplianceEmail, forceSendWithCompliance) =>
			updatePostRef.current?.(
				postOrId,
				selectedMoment,
				bypassCompliance,
				sendWithComplianceEmail,
				forceSendWithCompliance
			);

		contextRef.current = ctx;
		return ctx;
	});
	React.useEffect(() => {
		contextRef.current = context;
	});

	if (!setHasChangesRef.current) {
		setHasChangesRef.current = (value?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: value,
			}));
		};
	}

	if (!setAIReferenceRef.current) {
		setAIReferenceRef.current = (aiReference?: Api.IAIReference) => {
			setContext(currentContext => ({
				...currentContext,
				aiReference,
				hasChanges: true,
			}));
		};
	}

	if (!setContentRef.current) {
		setContentRef.current = (content?: string) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				postContent: content || '',
			}));
		};
	}
	if (!setImagesRef.current) {
		setImagesRef.current = (images?: Api.IFileAttachment[]) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				hasVideo: images?.some(x => x?.mimeType?.toLocaleLowerCase().startsWith('video/')),
				postImages: images || [],
				platformCustomizations: applyBaseAttachmentsToCustomizations(
					currentContext.platformCustomizations,
					currentContext.postImages || [],
					images || [],
					// Media added while the post is split belongs to the version being edited; media added
					// before the split still publishes everywhere.
					currentContext.customizeByPlatform
						? getActivePlatform(currentContext.postTargets, currentContext.activePlatform)
						: undefined
				),
			}));
		};
	}
	if (!replaceImageRef.current) {
		replaceImageRef.current = (originalId: string, replacement: Api.IFileAttachment) => {
			setContext(currentContext => {
				const nextImages = (currentContext.postImages || []).map(image =>
					image.id === originalId ? replacement : image
				);
				return {
					...currentContext,
					hasChanges: true,
					hasVideo: nextImages.some(image => image?.mimeType?.toLocaleLowerCase().startsWith('video/')),
					platformCustomizations: replaceAttachmentEverywhere(
						currentContext.platformCustomizations,
						originalId,
						replacement
					),
					postImages: nextImages,
				};
			});
		};
	}
	if (!setFacebookMentionsRef.current) {
		setFacebookMentionsRef.current = (mentions?: Api.IFacebookPageLookupResult[]) => {
			setContext(currentContext => ({
				...currentContext,
				facebookMentions: mentions || [],
				hasChanges: true,
			}));
		};
	}
	if (!loadContentRef.current) {
		loadContentRef.current = (content?: string) => {
			const parsed = parseFacebookMentions(content);
			setContext(currentContext => ({
				...currentContext,
				facebookMentions: parsed.mentions,
				hasChanges: true,
				postContent: parsed.content,
			}));
		};
	}
	if (!setSelectedCreatorRef.current) {
		setSelectedCreatorRef.current = (creator?: Api.IUser) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: false,
				selectedCreator: creator || null,
			}));
		};
	}
	if (!setTemplateRef.current) {
		setTemplateRef.current = (template?: Api.ITemplate) =>
			setContext(currentContext => ({
				...currentContext,
				template,
			}));
	}

	if (!setPostTargetsRef.current) {
		setPostTargetsRef.current = (targets?: Api.IPostTarget[]) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: false,
				postTargets: targets || [],
				// Targeting a platform while split is on has to give it an entry here: the server rejects a
				// missing one rather than publishing the base in its place.
				platformCustomizations: currentContext.customizeByPlatform
					? materializePlatformCustomizations(
							getTargetedPlatforms(targets),
							currentContext.platformCustomizations,
							currentContext.postContent,
							currentContext.postImages
						)
					: currentContext.platformCustomizations,
			}));
		};
	}

	if (!setCampaignNameRef.current) {
		setCampaignNameRef.current = (name?: string) => {
			setContext(currentContext => ({
				...currentContext,
				campaignName: name,
				hasChanges: false,
			}));
		};
	}

	if (!setIsReviewModeRef.current) {
		setIsReviewModeRef.current = (isReviewMode?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				isReviewMode,
			}));
		};
	}

	if (!setOverlayLogoRef.current) {
		setOverlayLogoRef.current = (overlayLogo?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				overlayLogo,
			}));
		};
	}
	if (!setLogoSizeRef.current) {
		setLogoSizeRef.current = (logoSize?: number) => {
			setContext(currentContext => ({
				...currentContext,
				logoSize,
				hasChanges: true,
			}));
		};
	}
	if (!setHideWhiteBackgroundRef.current) {
		setHideWhiteBackgroundRef.current = (hideWhiteBackground?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				hideWhiteBackground,
			}));
		};
	}

	if (!setCustomizeByPlatformRef.current) {
		setCustomizeByPlatformRef.current = (customizeByPlatform: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				customizeByPlatform,
				hasChanges: true,
				// Turning it off is destructive by contract: the versions go, and there is no retained copy.
				platformCustomizations: customizeByPlatform
					? materializePlatformCustomizations(
							getTargetedPlatforms(currentContext.postTargets),
							currentContext.platformCustomizations,
							currentContext.postContent,
							currentContext.postImages
						)
					: [],
			}));
		};
	}

	if (!setPlatformCustomizationRef.current) {
		setPlatformCustomizationRef.current = (
			platform: Api.SocialMediaType,
			next: Partial<IPlatformCustomizationDraft>
		) => {
			setContext(currentContext => {
				const existing = currentContext.platformCustomizations ?? [];
				const current =
					existing.find(customization => customization.platform === platform) ??
					createCustomizationFromBase(platform, currentContext.postContent, currentContext.postImages);
				const updated = { ...current, ...next, platform };
				return {
					...currentContext,
					hasChanges: true,
					platformCustomizations: existing.some(customization => customization.platform === platform)
						? existing.map(customization => (customization.platform === platform ? updated : customization))
						: [...existing, updated],
				};
			});
		};
	}

	if (!resetPlatformCustomizationRef.current) {
		resetPlatformCustomizationRef.current = (platform: Api.SocialMediaType) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				platformCustomizations: (currentContext.platformCustomizations ?? []).map(customization =>
					customization.platform === platform
						? createCustomizationFromBase(platform, currentContext.postContent, currentContext.postImages)
						: customization
				),
			}));
		};
	}

	if (!loadPlatformCustomizationsRef.current) {
		loadPlatformCustomizationsRef.current = (
			platformCustomizations: readonly Api.IPostPlatformCustomization[] | undefined
		) => {
			const { drafts, mentions } = fromApiPlatformCustomizations(platformCustomizations);
			setContext(currentContext => ({
				...currentContext,
				// Stored versions are what mark a post customized; an unsplit post stores none.
				customizeByPlatform: drafts.length > 0,
				facebookMentions: mergeFacebookMentions(currentContext.facebookMentions, mentions),
				platformCustomizations: drafts,
			}));
		};
	}

	if (!setActivePlatformRef.current) {
		setActivePlatformRef.current = (platform: Api.SocialMediaType) => {
			setContext(currentContext => ({
				...currentContext,
				activePlatform: platform,
			}));
		};
	}

	if (!schedulePostRef.current) {
		schedulePostRef.current = async (
			selectedMoment?: Moment,
			suggestionId?: string,
			impersonationContext?: Api.IImpersonationContext,
			bypasssCompliance?: boolean,
			sendWithComplianceEmail?: string,
			forceSendWithCompliance?: boolean
		) => {
			const basePost = getPostBase();
			const post: Api.ICreateSocialMediaPostRequest = {
				...basePost,
				dueDate: selectedMoment?.toISOString(),
				forUserId: contextRef.current?.selectedCreator?.id,
				// RegEd forces compliance on so the server builds the approval request the filing keys off,
				// independent of the legacy complianceSettings.enabled flag.
				sendWithCompliance:
					forceSendWithCompliance ||
					impersonationContext?.account?.preferences?.complianceSettings?.enabled ||
					userSession?.account?.preferences?.complianceSettings?.enabled
						? !bypasssCompliance
						: undefined,
				targets: undefined,
				sendWithComplianceEmail,
			};

			let error: Api.IOperationResultNoValue = null;
			let result: Api.SocialMediaPostViewModel = null;
			try {
				result = await Api.SocialMediaPostViewModel.schedulePost(userSession, post, suggestionId, impersonationContext);
				postNotification({
					info: result,
					topic: Topics.CREATE_SOCIAL_POST,
				});
			} catch (err) {
				error = err;
			}
			return [result, error] as const;
		};
	}

	if (!updatePostRef.current) {
		updatePostRef.current = async (
			postOrId: string | Api.SocialMediaPostViewModel,
			selectedMoment?: Moment,
			bypassCompliance?: boolean,
			sendWithComplianceEmail?: string,
			forceSendWithCompliance?: boolean
		) => {
			const viewModel =
				typeof postOrId === 'string' ? new Api.SocialMediaPostViewModel(userSession, { id: postOrId }) : postOrId;
			const basePost = getPostBase();
			// If sendWithComplianceEmail is provided, enable compliance (for Draft → Scheduled with compliance).
			// Otherwise preserve existing compliance state (can only bypass, not enable). RegEd forces it
			// on so the server builds the approval request the filing keys off.
			const sendWithCompliance = forceSendWithCompliance
				? !bypassCompliance
				: sendWithComplianceEmail
					? !bypassCompliance
					: viewModel.sendWithCompliance
						? !bypassCompliance
						: null;
			const updatedPost: Api.ISocialMediaPost = {
				...basePost,
				creator: viewModel.creator || contextRef.current.selectedCreator,
				dueDate: selectedMoment?.toISOString(),
				id: viewModel.id,
				name: contextRef.current.campaignName || viewModel.name || basePost.name,
				sendWithCompliance,
				sendWithComplianceEmail,
				status: viewModel.status,
				targets: [],
			};

			let error: Api.IOperationResultNoValue = null;
			let result: Api.IOperationResult<Api.ISocialMediaPost> = null;
			try {
				result = await viewModel.update(updatedPost);
				postNotification({
					info: viewModel,
					topic: Topics.EDIT_SOCIAL_POST,
				});
			} catch (err) {
				error = err;
			}
			return [result, error] as const;
		};
	}

	return [context, setContext] as const;
};
